import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { NexusIcon } from '../shared/ui/NexusIcon';
import { Btn, Pill } from '../shared/ui';
import { useToast } from '../shared/feedback';
import { PageHead, StatCard, TabBar, Th, TenancyModal } from '../platform/_ui';
import { orgWorkspaces } from '../platform/platformStore';
import { orgAiApi } from '../api/org-ai.api.js';
import { usePermissions } from './auth/usePermissions.js';
import { DeniedState } from './auth/DeniedState.jsx';

const maskKey = (k) => k ? k.substring(0, 3) + '...' + k.slice(-4) : '••••••••';

const KEY_PROVIDERS = ['OpenAI', 'Anthropic', 'Google', 'Mistral', 'Azure', 'Custom'];

const TABS = [
  { id: 'usage',     label: 'AI Usage',       icon: 'BarChart2'     },
  { id: 'agents',    label: 'AI Agents',       icon: 'Cpu'           },
  { id: 'knowledge', label: 'Knowledge Bases', icon: 'BookOpen'      },
  { id: 'prompts',   label: 'Prompt Library',  icon: 'MessageSquare' },
  { id: 'models',    label: 'AI Models',       icon: 'Zap'           },
  { id: 'keys',      label: 'API Keys',        icon: 'Key'           },
  { id: 'embeddings',label: 'Embeddings',      icon: 'GitMerge'      },
  { id: 'guardrails',label: 'Guardrails',      icon: 'Shield'        },
  { id: 'analytics', label: 'AI Analytics',    icon: 'TrendingUp'    },
];

const AGENT_TYPES       = ['Conversational', 'Task Agent', 'Retrieval Agent', 'Workflow Agent'];
const PROMPT_CATEGORIES = ['Sales', 'Support', 'Onboarding', 'Analysis', 'Summary', 'General'];

const PLATFORM_MODELS = [
  { id: 'claude-opus-4-8',   name: 'Claude Opus 4.8',  provider: 'Anthropic', context: '200k', status: 'active'    },
  { id: 'claude-sonnet-4-6', name: 'Claude Sonnet 4.6', provider: 'Anthropic', context: '200k', status: 'active'    },
  { id: 'claude-haiku-4-5',  name: 'Claude Haiku 4.5',  provider: 'Anthropic', context: '200k', status: 'active'    },
  { id: 'gpt-4o',            name: 'GPT-4o',            provider: 'OpenAI',    context: '128k', status: 'available' },
  { id: 'gpt-4o-mini',       name: 'GPT-4o Mini',       provider: 'OpenAI',    context: '128k', status: 'available' },
  { id: 'gemini-2-flash',    name: 'Gemini 2.0 Flash',  provider: 'Google',    context: '1M',   status: 'available' },
  { id: 'mistral-large',     name: 'Mistral Large',     provider: 'Mistral',   context: '128k', status: 'available' },
];

const fmt = (iso) => iso ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

function ComingSoonSection({ label }) {
  return (
    <div className="nx-empty" style={{ padding: '40px 0', flexDirection: 'column', gap: 6 }}>
      <NexusIcon name="Clock" size={22} />
      <span style={{ fontWeight: 600 }}>{label}</span>
      <span className="nx-muted nx-mini">This section will be available in the next AI Platform release.</span>
    </div>
  );
}

export default function OrgAI({ data }) {
  const { orgId }  = useParams();
  const navigate   = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo    = searchParams.get('returnTo');
  const returnLabel = searchParams.get('returnLabel') || 'Back';
  const toast = useToast();
  const perm = usePermissions();

  const [tab, setTab]           = useState('usage');
  const [wsFilter, setWsFilter] = useState('');
  const [agents, setAgents]     = useState([]);
  const [kbs, setKbs]           = useState([]);
  const [prompts, setPrompts]   = useState([]);
  const [usage, setUsage]       = useState([]);
  const [enabledModels, setEnabledModels] = useState(['claude-opus-4-8', 'claude-sonnet-4-6', 'claude-haiku-4-5']);
  const [apiKeys, setApiKeys]         = useState([]);
  const [addKeyModal, setAddKeyModal] = useState(false);
  const [keyForm, setKeyForm]         = useState({ provider: 'OpenAI', name: '', value: '' });
  const [createModal, setCreateModal] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', type: 'Conversational', category: 'General', content: '' });

  const loadAgents = useCallback(async () => {
    try { setAgents((await orgAiApi.listAgents(orgId)) || []); } catch { toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed to load data' }); }
  }, [orgId]);

  const loadKBs = useCallback(async () => {
    try { setKbs((await orgAiApi.listKBs(orgId)) || []); } catch { toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed to load data' }); }
  }, [orgId]);

  const loadPrompts = useCallback(async () => {
    try { setPrompts((await orgAiApi.listPrompts(orgId)) || []); } catch { toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed to load data' }); }
  }, [orgId]);

  const loadUsage = useCallback(async () => {
    try { setUsage((await orgAiApi.usage(orgId)) || []); } catch { toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed to load data' }); }
  }, [orgId]);

  const loadKeys = useCallback(async () => {
    try { setApiKeys((await orgAiApi.listKeys(orgId)) || []); } catch { toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed to load data' }); }
  }, [orgId]);

  useEffect(() => { loadAgents(); loadKBs(); loadPrompts(); }, [loadAgents, loadKBs, loadPrompts]);
  useEffect(() => { if (tab === 'usage') loadUsage(); }, [tab, loadUsage]);
  useEffect(() => { if (tab === 'keys') loadKeys(); }, [tab, loadKeys]);

  const org = data.state.organizations.find(o => o.id === orgId);
  const wss = orgWorkspaces(data.state, orgId);
  if (!org) return null;

  const isAdmin = perm.flags.canManageAI || perm.isSA;

  if (!isAdmin) return (
    <div className="nx-cc-page">
      <DeniedState
        reason="insufficient_permission"
        message="AI Platform configuration requires Organization Admin access or the ai.* capability set."
      />
    </div>
  );

  const filteredWss = wsFilter ? wss.filter(w => w.id === wsFilter) : wss;
  const resetForm = () => setForm({ name: '', description: '', type: 'Conversational', category: 'General', content: '' });

  const handleAddKey = async () => {
    if (!keyForm.value.trim()) return;
    try {
      const saved = await orgAiApi.addKey(orgId, { provider: keyForm.provider, name: keyForm.name.trim() || keyForm.provider + ' Key', value: keyForm.value.trim() });
      setApiKeys(ks => [...ks, saved]);
      toast({ tone: 'success', icon: 'Key', title: 'API key saved', desc: keyForm.provider });
      setAddKeyModal(false);
      setKeyForm({ provider: 'OpenAI', name: '', value: '' });
    } catch (e) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed to save key', desc: e?.message });
    }
  };

  const handleCreateAgent = async () => {
    if (!form.name.trim()) return;
    try {
      await orgAiApi.createAgent(orgId, { name: form.name.trim(), description: form.description, type: form.type });
      await loadAgents();
      toast({ tone: 'success', icon: 'Check', title: 'Agent created', desc: form.name });
      setCreateModal(null); resetForm();
    } catch (e) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed to create agent', desc: e.message });
    }
  };

  const handleEditAgent = async () => {
    if (!form.name.trim() || !editItem) return;
    try {
      await orgAiApi.updateAgent(orgId, editItem.id, { name: form.name.trim(), description: form.description, type: form.type });
      await loadAgents();
      toast({ tone: 'success', icon: 'Check', title: 'Agent updated' });
      setEditItem(null); setCreateModal(null); resetForm();
    } catch (e) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed to update agent', desc: e.message });
    }
  };

  const handleCreateKB = async () => {
    if (!form.name.trim()) return;
    try {
      await orgAiApi.createKB(orgId, { name: form.name.trim(), description: form.description });
      await loadKBs();
      toast({ tone: 'success', icon: 'Check', title: 'Knowledge base created', desc: form.name });
      setCreateModal(null); resetForm();
    } catch (e) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed to create knowledge base', desc: e.message });
    }
  };

  const handleCreatePrompt = async () => {
    if (!form.name.trim()) return;
    try {
      await orgAiApi.createPrompt(orgId, { name: form.name.trim(), category: form.category, content: form.content });
      await loadPrompts();
      toast({ tone: 'success', icon: 'Check', title: 'Prompt saved', desc: form.name });
      setCreateModal(null); resetForm();
    } catch (e) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed to save prompt', desc: e.message });
    }
  };

  const handleDelete = async () => {
    const { id, type } = deleteConfirm;
    try {
      if (type === 'agent')  { await orgAiApi.deleteAgent(orgId, id);  await loadAgents();  }
      if (type === 'kb')     { await orgAiApi.deleteKB(orgId, id);     await loadKBs();     }
      if (type === 'prompt') { await orgAiApi.deletePrompt(orgId, id); await loadPrompts(); }
      if (type === 'key')    { await orgAiApi.deleteKey(orgId, id);    setApiKeys(ks => ks.filter(k => k.id !== id)); }
      toast({ tone: 'warning', icon: 'Trash', title: 'Deleted', desc: deleteConfirm.name });
      setDeleteConfirm(null);
    } catch (e) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed to delete', desc: e.message });
    }
  };

  const toggleModel = (modelId) => {
    setEnabledModels(prev => prev.includes(modelId) ? prev.filter(x => x !== modelId) : [...prev, modelId]);
    toast({ tone: 'success', icon: 'Zap', title: 'Model preference updated' });
  };

  return (
    <div className="nx-cc-page">
      <PageHead
        title="AI Platform"
        sub={'AI configuration and usage for ' + org.name}
        back={returnTo ? { label: `Back to ${returnLabel}`, onClick: () => navigate(returnTo) } : undefined}
        actions={
          tab === 'agents'    ? <Btn variant="primary" icon="Plus" onClick={() => { resetForm(); setCreateModal('agent');  }}>Create Agent</Btn>         :
          tab === 'knowledge' ? <Btn variant="primary" icon="Plus" onClick={() => { resetForm(); setCreateModal('kb');     }}>Create Knowledge Base</Btn> :
          tab === 'prompts'   ? <Btn variant="primary" icon="Plus" onClick={() => { resetForm(); setCreateModal('prompt'); }}>Add Prompt</Btn>            :
          tab === 'keys'      ? <Btn variant="primary" icon="Plus" onClick={() => { setKeyForm({ provider: 'OpenAI', name: '', value: '' }); setAddKeyModal(true); }}>Add API Key</Btn> : null
        }
      />

      <div className="nx-cc-stat-grid nx-kpi-4">
        <StatCard label="AI Agents"        value={agents.length}        icon="Cpu"           color="purple" />
        <StatCard label="Knowledge Bases"  value={kbs.length}           icon="BookOpen"      color="blue"   />
        <StatCard label="Prompt Templates" value={prompts.length}       icon="MessageSquare" color="blue"   />
        <StatCard label="Active Models"    value={enabledModels.length} icon="Zap"           color="amber"  />
      </div>

      <TabBar tabs={TABS} active={tab} onChange={setTab} />
      <div className="nx-cc-tab-body">

        {tab === 'usage' && (
          <div>
            <div style={{ display: 'flex', gap: 10, marginBottom: 14, alignItems: 'center', flexWrap: 'wrap' }}>
              <select className="nx-input" style={{ width: 220, fontSize: 13 }} value={wsFilter} onChange={e => setWsFilter(e.target.value)}>
                <option value="">All Workspaces</option>
                {wss.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
              </select>
            </div>
            {filteredWss.length === 0 ? (
              <div className="nx-empty"><NexusIcon name="LayoutGrid" size={20} /><span>No workspaces found.</span></div>
            ) : (
              <div className="nx-table-wrap"><div className="nx-table-scroll"><table className="nx-table">
                <thead><tr>
                  <Th icon="LayoutGrid">Workspace</Th>
                  <Th icon="Cpu">Agents</Th>
                  <Th icon="BookOpen">Knowledge Bases</Th>
                  <Th icon="Activity">Requests</Th>
                  <Th icon="Hash">Tokens</Th>
                  <Th icon="DollarSign">Est. Cost</Th>
                </tr></thead>
                <tbody>{filteredWss.map(w => {
                  const u = usage.find ? usage.find(x => x.workspaceId === w.id) : null;
                  return (
                    <tr key={w.id} className="nx-tr">
                      <td>
                        <span className="nx-row-name">
                          <span className="nx-domain-mark" style={{ width: 24, height: 24, fontSize: 10, background: 'rgba(110,63,186,0.10)', color: '#6E3FBA' }}>{(w.name || 'W')[0]}</span>
                          <span className="nx-row-name-text">{w.name}</span>
                        </span>
                      </td>
                      <td><span className="nx-muted">{u?.agentCount ?? '—'}</span></td>
                      <td><span className="nx-muted">{u?.kbCount ?? '—'}</span></td>
                      <td><span className="nx-muted">{u?.requestCount ?? '—'}</span></td>
                      <td><span className="nx-muted">{u?.totalTokens ?? '—'}</span></td>
                      <td><span className="nx-muted">{u?.estimatedCost != null ? '$' + u.estimatedCost.toFixed(2) : '—'}</span></td>
                    </tr>
                  );
                })}</tbody>
              </table></div></div>
            )}
          </div>
        )}

        {tab === 'agents' && (
          agents.length === 0 ? (
            <div className="nx-empty" style={{ flexDirection: 'column', gap: 8 }}>
              <NexusIcon name="Cpu" size={24} />
              <span style={{ fontWeight: 600 }}>No AI agents yet</span>
              <span className="nx-muted nx-mini">Create agents that can be deployed across workspaces.</span>
              <Btn variant="secondary" icon="Plus" onClick={() => { resetForm(); setCreateModal('agent'); }}>Create First Agent</Btn>
            </div>
          ) : (
            <div className="nx-table-wrap"><div className="nx-table-scroll"><table className="nx-table">
              <thead><tr>
                <Th icon="Cpu">Agent</Th>
                <Th icon="Tag">Type</Th>
                <Th icon="Activity">Status</Th>
                <Th icon="Calendar">Created</Th>
                <Th end />
              </tr></thead>
              <tbody>{agents.map(a => (
                <tr key={a.id} className="nx-tr">
                  <td>
                    <div>
                      <div className="nx-row-name-text">{a.name}</div>
                      {a.description && <div className="nx-muted nx-mini">{a.description}</div>}
                    </div>
                  </td>
                  <td><Pill tone="blue">{a.type || 'Conversational'}</Pill></td>
                  <td><Pill tone="green">{a.status || 'active'}</Pill></td>
                  <td><span className="nx-muted nx-mini">{fmt(a.createdAt || a.created)}</span></td>
                  <td className="nx-td-end" onClick={e => e.stopPropagation()}>
                    <Btn variant="ghost" size="sm" icon="Pencil" onClick={() => { setForm({ name: a.name, description: a.description || '', type: a.type || 'Conversational', category: 'General', content: '' }); setEditItem(a); setCreateModal('agent'); }} />
                    <Btn variant="ghost" size="sm" icon="Trash" onClick={() => setDeleteConfirm({ id: a.id, name: a.name, type: 'agent' })} />
                  </td>
                </tr>
              ))}</tbody>
            </table></div></div>
          )
        )}

        {tab === 'knowledge' && (
          kbs.length === 0 ? (
            <div className="nx-empty" style={{ flexDirection: 'column', gap: 8 }}>
              <NexusIcon name="BookOpen" size={24} />
              <span style={{ fontWeight: 600 }}>No knowledge bases yet</span>
              <span className="nx-muted nx-mini">Create knowledge bases to power your AI agents with org-specific information.</span>
              <Btn variant="secondary" icon="Plus" onClick={() => { resetForm(); setCreateModal('kb'); }}>Create First Knowledge Base</Btn>
            </div>
          ) : (
            <div className="nx-table-wrap"><div className="nx-table-scroll"><table className="nx-table">
              <thead><tr>
                <Th icon="BookOpen">Knowledge Base</Th>
                <Th icon="FileText">Documents</Th>
                <Th icon="Activity">Status</Th>
                <Th icon="Calendar">Created</Th>
                <Th end />
              </tr></thead>
              <tbody>{kbs.map(k => (
                <tr key={k.id} className="nx-tr">
                  <td>
                    <div>
                      <div className="nx-row-name-text">{k.name}</div>
                      {k.description && <div className="nx-muted nx-mini">{k.description}</div>}
                    </div>
                  </td>
                  <td><span className="nx-muted">{k.documentCount ?? k.documents ?? 0}</span></td>
                  <td><Pill tone="green">{k.status || 'active'}</Pill></td>
                  <td><span className="nx-muted nx-mini">{fmt(k.createdAt || k.created)}</span></td>
                  <td className="nx-td-end" onClick={e => e.stopPropagation()}>
                    <Btn variant="ghost" size="sm" icon="Trash" onClick={() => setDeleteConfirm({ id: k.id, name: k.name, type: 'kb' })} />
                  </td>
                </tr>
              ))}</tbody>
            </table></div></div>
          )
        )}

        {tab === 'prompts' && (
          prompts.length === 0 ? (
            <div className="nx-empty" style={{ flexDirection: 'column', gap: 8 }}>
              <NexusIcon name="MessageSquare" size={24} />
              <span style={{ fontWeight: 600 }}>No prompt templates yet</span>
              <span className="nx-muted nx-mini">Save reusable prompt templates for consistent AI interactions across workspaces.</span>
              <Btn variant="secondary" icon="Plus" onClick={() => { resetForm(); setCreateModal('prompt'); }}>Add First Prompt</Btn>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px,1fr))', gap: 12 }}>
              {prompts.map(p => (
                <div key={p.id} className="nx-cc-chart-card" style={{ padding: 14 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 8 }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 3 }}>{p.name}</div>
                      <Pill tone="blue">{p.category || 'General'}</Pill>
                    </div>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <Btn variant="ghost" size="sm" icon="Copy" onClick={() => { navigator.clipboard?.writeText(p.content || ''); toast({ tone: 'success', icon: 'Copy', title: 'Copied' }); }} />
                      <Btn variant="ghost" size="sm" icon="Trash" onClick={() => setDeleteConfirm({ id: p.id, name: p.name, type: 'prompt' })} />
                    </div>
                  </div>
                  {p.content && <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', lineHeight: 1.5, maxHeight: 60, overflow: 'hidden' }}>{p.content}</div>}
                  <div className="nx-muted nx-mini" style={{ marginTop: 6 }}>{fmt(p.createdAt || p.created)}</div>
                </div>
              ))}
            </div>
          )
        )}

        {tab === 'models' && (
          <div>
            <div style={{ marginBottom: 10, padding: '8px 12px', borderRadius: 6, background: 'var(--nx-accent-tint)', fontSize: 12, color: 'var(--nx-text-muted)' }}>
              Configure which AI models are available across your organization's workspaces.
            </div>
            <div className="nx-table-wrap"><div className="nx-table-scroll"><table className="nx-table">
              <thead><tr>
                <Th icon="Zap">Model</Th>
                <Th icon="Building2">Provider</Th>
                <Th icon="Hash">Context</Th>
                <Th icon="Activity">Status</Th>
                <Th end />
              </tr></thead>
              <tbody>{PLATFORM_MODELS.map(m => {
                const enabled = enabledModels.includes(m.id);
                return (
                  <tr key={m.id} className="nx-tr">
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ width: 28, height: 28, borderRadius: 6, background: 'rgba(110,63,186,0.10)', color: '#6E3FBA', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <NexusIcon name="Zap" size={13} />
                        </div>
                        <span className="nx-row-name-text">{m.name}</span>
                      </div>
                    </td>
                    <td><span className="nx-muted">{m.provider}</span></td>
                    <td><span className="nx-muted">{m.context}</span></td>
                    <td><Pill tone={enabled ? 'green' : 'gray'}>{enabled ? 'Enabled' : 'Disabled'}</Pill></td>
                    <td className="nx-td-end">
                      <Btn variant={enabled ? 'ghost' : 'secondary'} size="sm" icon={enabled ? 'X' : 'Check'} onClick={() => toggleModel(m.id)}>
                        {enabled ? 'Disable' : 'Enable'}
                      </Btn>
                    </td>
                  </tr>
                );
              })}</tbody>
            </table></div></div>
          </div>
        )}

        {tab === 'keys' && (
          apiKeys.length === 0 ? (
            <div className="nx-empty" style={{ flexDirection: 'column', gap: 8 }}>
              <NexusIcon name="Key" size={24} />
              <span style={{ fontWeight: 600 }}>No API keys saved</span>
              <span className="nx-muted nx-mini">Add provider API keys to enable AI model integrations.</span>
              <Btn variant="secondary" icon="Plus" onClick={() => { setKeyForm({ provider: 'OpenAI', name: '', value: '' }); setAddKeyModal(true); }}>Add First Key</Btn>
            </div>
          ) : (
            <div className="nx-table-wrap"><div className="nx-table-scroll"><table className="nx-table">
              <thead><tr>
                <Th icon="Building2">Provider</Th>
                <Th icon="Tag">Name</Th>
                <Th icon="Key">Key</Th>
                <Th icon="Calendar">Added</Th>
                <Th end />
              </tr></thead>
              <tbody>{apiKeys.map(k => (
                <tr key={k.id} className="nx-tr">
                  <td><Pill tone="gray">{k.provider || '—'}</Pill></td>
                  <td><span className="nx-row-name-text">{k.name || k.provider + ' Key'}</span></td>
                  <td><code style={{ fontSize: 12, fontFamily: 'monospace', color: 'var(--nx-text-muted)' }}>{maskKey(k.value || k.maskedKey || k.key)}</code></td>
                  <td><span className="nx-muted nx-mini">{fmt(k.createdAt || k.addedAt)}</span></td>
                  <td className="nx-td-end">
                    <Btn variant="ghost" size="sm" icon="Trash" onClick={() => setDeleteConfirm({ id: k.id, name: k.name || k.provider + ' Key', type: 'key' })} />
                  </td>
                </tr>
              ))}</tbody>
            </table></div></div>
          )
        )}

        {tab === 'embeddings' && <ComingSoonSection label="Embeddings Configuration" />}
        {tab === 'guardrails' && <ComingSoonSection label="AI Guardrails" />}
        {tab === 'analytics'  && <ComingSoonSection label="AI Analytics" />}
      </div>

      {createModal === 'agent' && (
        <TenancyModal title={editItem ? 'Edit Agent' : 'Create AI Agent'} icon="Cpu" onClose={() => { setCreateModal(null); setEditItem(null); resetForm(); }}>
          <div className="nx-form" style={{ gap: 10, marginTop: 8 }}>
            <div className="nx-form-row">
              <label>Agent Name *</label>
              <input className="nx-input" autoFocus value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Sales Assistant" />
            </div>
            <div className="nx-form-row">
              <label>Description</label>
              <textarea className="nx-input" rows={2} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="What does this agent do?" />
            </div>
            <div className="nx-form-row">
              <label>Type</label>
              <select className="nx-input" value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}>
                {AGENT_TYPES.map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
          </div>
          <div className="nx-confirm-actions" style={{ marginTop: 14 }}>
            <Btn variant="ghost" onClick={() => { setCreateModal(null); setEditItem(null); resetForm(); }}>Cancel</Btn>
            <Btn variant="primary" icon="Check" disabled={!form.name.trim()} onClick={editItem ? handleEditAgent : handleCreateAgent}>
              {editItem ? 'Save Changes' : 'Create Agent'}
            </Btn>
          </div>
        </TenancyModal>
      )}

      {createModal === 'kb' && (
        <TenancyModal title="Create Knowledge Base" icon="BookOpen" onClose={() => { setCreateModal(null); resetForm(); }}>
          <div className="nx-form" style={{ gap: 10, marginTop: 8 }}>
            <div className="nx-form-row">
              <label>Name *</label>
              <input className="nx-input" autoFocus value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Product Documentation" />
            </div>
            <div className="nx-form-row">
              <label>Description</label>
              <textarea className="nx-input" rows={2} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="What content will this knowledge base contain?" />
            </div>
          </div>
          <div className="nx-confirm-actions" style={{ marginTop: 14 }}>
            <Btn variant="ghost" onClick={() => { setCreateModal(null); resetForm(); }}>Cancel</Btn>
            <Btn variant="primary" icon="Check" disabled={!form.name.trim()} onClick={handleCreateKB}>Create</Btn>
          </div>
        </TenancyModal>
      )}

      {createModal === 'prompt' && (
        <TenancyModal title="Add Prompt Template" icon="MessageSquare" onClose={() => { setCreateModal(null); resetForm(); }} width={560}>
          <div className="nx-form" style={{ gap: 10, marginTop: 8 }}>
            <div className="nx-form-row">
              <label>Name *</label>
              <input className="nx-input" autoFocus value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Sales Follow-up" />
            </div>
            <div className="nx-form-row">
              <label>Category</label>
              <select className="nx-input" value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
                {PROMPT_CATEGORIES.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div className="nx-form-row" style={{ alignItems: 'flex-start' }}>
              <label>Prompt Content</label>
              <textarea className="nx-input" rows={5} value={form.content} onChange={e => setForm(f => ({ ...f, content: e.target.value }))} placeholder="Enter your prompt template. Use {{variable}} for dynamic values." />
            </div>
          </div>
          <div className="nx-confirm-actions" style={{ marginTop: 14 }}>
            <Btn variant="ghost" onClick={() => { setCreateModal(null); resetForm(); }}>Cancel</Btn>
            <Btn variant="primary" icon="Check" disabled={!form.name.trim()} onClick={handleCreatePrompt}>Save Prompt</Btn>
          </div>
        </TenancyModal>
      )}

      {addKeyModal && (
        <TenancyModal title="Add API Key" icon="Key" onClose={() => setAddKeyModal(false)}>
          <div className="nx-form" style={{ gap: 10, marginTop: 8 }}>
            <div className="nx-form-row">
              <label>Provider</label>
              <select className="nx-input" value={keyForm.provider} onChange={e => setKeyForm(f => ({ ...f, provider: e.target.value }))}>
                {KEY_PROVIDERS.map(p => <option key={p}>{p}</option>)}
              </select>
            </div>
            <div className="nx-form-row">
              <label>Key Name</label>
              <input className="nx-input" placeholder="e.g. Production Key" value={keyForm.name} onChange={e => setKeyForm(f => ({ ...f, name: e.target.value }))} />
            </div>
            <div className="nx-form-row">
              <label>API Key <span style={{ color: 'var(--nx-danger)' }}>*</span></label>
              <input className="nx-input" type="password" autoComplete="off" placeholder="sk-..." value={keyForm.value} onChange={e => setKeyForm(f => ({ ...f, value: e.target.value }))} />
            </div>
          </div>
          <div className="nx-confirm-actions" style={{ marginTop: 14 }}>
            <Btn variant="ghost" onClick={() => setAddKeyModal(false)}>Cancel</Btn>
            <Btn variant="primary" icon="Key" disabled={!keyForm.value.trim()} onClick={handleAddKey}>Save Key</Btn>
          </div>
        </TenancyModal>
      )}

      {deleteConfirm && (
        <TenancyModal title={'Delete ' + deleteConfirm.name + '?'} icon="Trash" danger onClose={() => setDeleteConfirm(null)}>
          <p className="nx-confirm-desc">This action cannot be undone.</p>
          <div className="nx-confirm-actions">
            <Btn variant="ghost" onClick={() => setDeleteConfirm(null)}>Cancel</Btn>
            <Btn variant="danger" icon="Trash" onClick={handleDelete}>Delete</Btn>
          </div>
        </TenancyModal>
      )}
    </div>
  );
}
